// The label section of a return step (2026-10-27): who the label is emailed
// to, the internal copy, and the packages (one label per box). One component
// for the main panel's Process return/refund step (SuggestionPanel.jsx) and
// the agent's return step (ShadowDraftPanel.jsx), so the two panels can't
// drift. When it shows is returnStepCheck.js's returnLabelFieldsShown().
//
// LabelEmailInputs is the "Send label to" / "Also send a copy to" / "Cc
// address" trio that SuggestionPanel.jsx's LabelEmailFields always was
// (feature/website-rma-label-email-step1), now value-based: value { email,
// ccInternalCopy, ccAddress }, onChange(patch). The executor reads the same
// three keys (action-executor.js: email, ccInternalCopy, ccAddress; an empty
// cc falls back to the brand's service@ address).
import { Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material'
import PackagesEditor from './PackagesEditor.jsx'
import { m365ServiceAddressForCompany } from './constants/companies.js'
import { labelFieldErrors } from './returnStepCheck.js'

export function LabelEmailInputs({ value = {}, onChange, defaultEmail, ccCompany, disabled = false, showErrors = false, testIdPrefix = 'label' }) {
  const email = value.email ?? defaultEmail ?? ''
  const ccOn = value.ccInternalCopy ?? true
  const ccAddress = value.ccAddress ?? m365ServiceAddressForCompany(ccCompany)
  const errors = showErrors ? labelFieldErrors({ email, ccInternalCopy: ccOn, ccAddress }) : {}
  return (
    <>
      <TextField
        size="small"
        label="Send label to"
        type="email"
        value={email}
        onChange={(ev) => onChange({ email: ev.target.value })}
        disabled={disabled}
        error={Boolean(errors.email)}
        helperText={errors.email ?? undefined}
        inputProps={{ 'data-testid': `${testIdPrefix}-send-to` }}
        fullWidth
        sx={{ mt: 1 }}
      />
      <FormControlLabel
        sx={{ display: 'block', mt: 0.25 }}
        control={
          <Checkbox
            size="small"
            checked={ccOn}
            onChange={(ev) => onChange({ ccInternalCopy: ev.target.checked })}
            disabled={disabled}
            inputProps={{ 'data-testid': `${testIdPrefix}-cc-on` }}
          />
        }
        label="Also send a copy to"
      />
      {ccOn && (
        <TextField
          size="small"
          label="Cc address"
          type="email"
          // Defaults to the brand's service@ address; the executor falls
          // back to the same address when this is left empty.
          value={ccAddress}
          onChange={(ev) => onChange({ ccAddress: ev.target.value })}
          disabled={disabled}
          error={Boolean(errors.ccAddress)}
          helperText={errors.ccAddress ?? undefined}
          inputProps={{ 'data-testid': `${testIdPrefix}-cc-address` }}
          fullWidth
          sx={{ mt: 0.25 }}
        />
      )}
    </>
  )
}

// packages: [{ weight }]; onPackagesChange(next). showEmail: false when no
// label is emailed (the customer ships their own: the packages still price
// the options). renderPackageExtra(i, pkg): the main panel's per-package
// rate quote.
export default function ReturnLabelFields({ value = {}, onChange, packages, onPackagesChange, defaultEmail, ccCompany, disabled = false, showEmail = true, showErrors = false, renderPackageExtra = null, weightNote = null, testId = 'return-label-fields', testIdPrefix = 'label' }) {
  const list = Array.isArray(packages) && packages.length ? packages : [{ weight: '' }]
  const errors = showErrors ? labelFieldErrors({ email: 'x@x.xx', packages: list }) : {}
  const packageErrors = Object.entries(errors).filter(([k]) => k.startsWith('package')).map(([, v]) => v)
  return (
    <Box data-testid={testId} sx={{ mt: 1 }}>
      <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>Packages (one label per box)</Typography>
      <PackagesEditor packages={list} onChange={onPackagesChange} disabled={disabled} renderExtra={renderPackageExtra} />
      {packageErrors.map((e) => <Typography key={e} variant="caption" color="error" display="block" data-testid={`${testIdPrefix}-package-error`}>{e}</Typography>)}
      {weightNote && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{weightNote}</Typography>}
      {showEmail && (
        <LabelEmailInputs value={value} onChange={onChange} defaultEmail={defaultEmail} ccCompany={ccCompany} disabled={disabled} showErrors={showErrors} testIdPrefix={testIdPrefix} />
      )}
    </Box>
  )
}
